import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { getOkkSettings, updateOkkSettings } from "@/api/okk_receiving";
import { searchStructuralUnits } from "@/api/one_c_pickers";
import { RemotePicker, type PickerRow } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiErrorText, LoadState } from "@/lib/scan-ui/screen";

const KEY = ["okk-settings"] as const;

/**
 * ОКК: склад арбитражных образцов — куда документ `ПеремещениеЗапасов`
 * переносит образцы «в архив» (`GET/PUT /api/okk-receiving/settings`, админ).
 *
 * Склад проверяется в 1С (существует, тип «Склад», не помечен) — отказ 422
 * показываем текстом сервера под полем. Не настроен — оформление образцов
 * отвечает 409, склад ниоткуда не подставляется.
 */
export function OkkArbitrageCard(): React.ReactElement {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: getOkkSettings });
  const [value, setValue] = useState<PickerRow | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (q.data) setValue(q.data.arbitrage_warehouse);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => updateOkkSettings({ arbitrage_warehouse_ref: value?.ref ?? null }),
    onMutate: () => setError(""),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      toast.success("Склад арбитражных образцов сохранён");
    },
    onError: (e) => setError(apiErrorText(e)),
  });

  const saved = q.data?.arbitrage_warehouse?.ref ?? null;
  const dirty = (value?.ref ?? null) !== saved;

  return (
    <Card>
      <CardHeader>
        <CardTitle>ОКК: склад арбитражных образцов</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!q.data ? (
          <LoadState
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            className="flex flex-col items-center gap-3 p-2 text-center"
          />
        ) : (
          <>
            <RemotePicker
              value={value}
              onChange={(row) => {
                setValue(row);
                setError("");
              }}
              search={(text) => searchStructuralUnits("Склад", text)}
              queryKey={["okk-arbitrage-warehouse"]}
              placeholder="Склад «Арбитражная»"
            />
            <p className="text-xs text-muted-foreground">
              Куда ОКК переносит образцы «в архив» документом ПеремещениеЗапасов в 1С. Пока
              склад не выбран, оформить образцы нельзя.
            </p>
            {!saved && (
              <p className="text-xs text-amber-800">Не настроен — оформление образцов недоступно.</p>
            )}
            {error && (
              <div role="alert" className="text-sm text-rose-700">
                {error}
              </div>
            )}
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              Сохранить
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
